import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useNotification } from '../../notification/NotificationContext';
import {
  forgotPassword, verifyOtp, resetPassword,
} from '../../services/notificationService';
import CharacterScene from './AuthCharacters';
import AuthIntroAnimation from './AuthIntroAnimation';
import { MailIcon, LockIcon, EyeIcon, EyeOffIcon, Spinner } from './AuthIcons';
import './AuthPortal.css';
import './ForgotPasswordPage.css';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [otpCode, setOtpCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [actionFailed, setActionFailed] = useState(false);
  const [introDone, setIntroDone] = useState(() => {
    return sessionStorage.getItem('authIntroPlayed') === '1';
  });
  const initiallySkippedIntroRef = useRef(introDone);

  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [isEmailFocused, setIsEmailFocused] = useState(false);
  const inputRefs = useRef([]);

  const isTyping = isPasswordFocused || isEmailFocused;
  const passwordValue = newPassword || confirmPassword;
  const passwordRevealed = showNewPwd || showConfirmPwd;

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  useEffect(() => {
    if (!actionFailed) return;
    const t = setTimeout(() => setActionFailed(false), 1800);
    return () => clearTimeout(t);
  }, [actionFailed]);

  const handleIntroComplete = () => {
    sessionStorage.setItem('authIntroPlayed', '1');
    setIntroDone(true);
  };

  const triggerFail = (msg) => {
    setError(msg);
    setActionFailed(true);
  };

  const sendOtp = async (silent = false) => {
    setError('');
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      triggerFail('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    try {
      await forgotPassword(email);
      setResendCooldown(60);
      if (!silent) {
        showNotification('If an account exists, an OTP has been sent.', 'success');
        setStep(2);
      } else {
        showNotification('OTP resent.', 'success');
      }
    } catch (err) {
      triggerFail(err.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleDigit = (i, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...otpDigits];
    next[i] = digit;
    setOtpDigits(next);
    setError('');
    if (digit && i < 5) inputRefs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !otpDigits[i] && i > 0) {
      inputRefs.current[i - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (text.length === 6) {
      e.preventDefault();
      setOtpDigits(text.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  const verify = async () => {
    setError('');
    const code = otpDigits.join('');
    if (code.length !== 6) {
      triggerFail('Enter the full 6-digit code.');
      return;
    }
    setLoading(true);
    try {
      await verifyOtp(email, code);
      setOtpCode(code);
      setStep(3);
    } catch (err) {
      triggerFail(err.message || 'Invalid or expired OTP');
    } finally {
      setLoading(false);
    }
  };

  const submitNewPassword = async () => {
    setError('');
    if (newPassword.length < 6) {
      triggerFail('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      triggerFail('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await resetPassword(email, otpCode, newPassword);
      showNotification('Password reset successfully. Please log in.', 'success');
      setTimeout(() => navigate('/login'), 800);
    } catch (err) {
      triggerFail(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    if (step > 1) {
      setStep(step - 1);
      setError('');
    } else {
      navigate('/login');
    }
  };

  const stepCopy = {
    1: { title: 'Forgot password?', sub: "No worries — we'll send a 6-digit code to your email." },
    2: { title: 'Enter the code', sub: <>We sent a code to <strong>{email}</strong>. It expires in 15 minutes.</> },
    3: { title: 'Set a new password', sub: 'Choose something strong (at least 6 characters).' },
  };

  return (
    <>
      {!introDone && <AuthIntroAnimation onComplete={handleIntroComplete} />}
      <div className="auth-portal">
        <div className="auth-content">
          <CharacterScene
            isTyping={isTyping}
            password={passwordValue}
            showPassword={passwordRevealed}
            loginFailed={actionFailed}
            entering={introDone}
          />

          <motion.div
            className="form-section"
            initial={initiallySkippedIntroRef.current ? false : { opacity: 0, x: 24 }}
            animate={introDone ? { opacity: 1, x: 0 } : { opacity: 0, x: 24 }}
            transition={{ duration: 0.55, delay: initiallySkippedIntroRef.current ? 0 : 0.65, ease: 'easeOut' }}
          >
            <button type="button" className="fp-back-link" onClick={goBack}>
              ← {step > 1 ? 'Back' : 'Back to log in'}
            </button>

            <div className="fp-stepper">
              {[1, 2, 3].map((n) => (
                <React.Fragment key={n}>
                  <span className={`fp-step ${step === n ? 'fp-step-active' : ''} ${step > n ? 'fp-step-done' : ''}`}>
                    {step > n ? '✓' : n}
                  </span>
                  {n < 3 && <span className={`fp-step-bar ${step > n ? 'fp-step-bar-done' : ''}`} />}
                </React.Fragment>
              ))}
            </div>

            <div className="form-header">
              <div className="sparkle-icon">✦</div>
              <h1>{stepCopy[step].title}</h1>
              <p className="subtitle">{stepCopy[step].sub}</p>
            </div>

            {step === 1 && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  sendOtp(false);
                }}
              >
                <div className={`form-group ${actionFailed ? 'has-error' : ''}`}>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(''); }}
                    onFocus={() => setIsEmailFocused(true)}
                    onBlur={() => setIsEmailFocused(false)}
                    placeholder=" "
                    autoFocus
                    required
                  />
                  <span className="input-icon"><MailIcon /></span>
                  <label className={email ? 'float-up' : ''}>Email</label>
                </div>

                {error && <div className="fp-err">{error}</div>}

                <button type="submit" className="login-btn" disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner />
                      Sending code...
                    </>
                  ) : (
                    'Send code'
                  )}
                </button>

                <div className="signup-link">
                  Remember your password? <Link to="/login">Log in</Link>
                </div>
              </form>
            )}

            {step === 2 && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  verify();
                }}
              >
                <div className="form-group" style={{ paddingTop: 0 }}>
                  <div className="fp-otp" onPaste={handlePaste}>
                    {otpDigits.map((d, i) => (
                      <input
                        key={i}
                        ref={(el) => (inputRefs.current[i] = el)}
                        className="fp-otp-box"
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={d}
                        onChange={(e) => handleDigit(i, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(i, e)}
                        autoFocus={i === 0}
                      />
                    ))}
                  </div>
                </div>

                {error && <div className="fp-err">{error}</div>}

                <button type="submit" className="login-btn" disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner />
                      Verifying...
                    </>
                  ) : (
                    'Verify code'
                  )}
                </button>

                <button
                  type="button"
                  className="fp-resend"
                  onClick={() => sendOtp(true)}
                  disabled={resendCooldown > 0 || loading}
                >
                  {resendCooldown > 0
                    ? `Resend in ${resendCooldown}s`
                    : "Didn't get it? Resend code"}
                </button>
              </form>
            )}

            {step === 3 && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  submitNewPassword();
                }}
              >
                <div className={`form-group ${actionFailed ? 'has-error' : ''}`}>
                  <input
                    type={showNewPwd ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                    onFocus={() => setIsPasswordFocused(true)}
                    onBlur={() => setIsPasswordFocused(false)}
                    placeholder=" "
                    required
                  />
                  <span className="input-icon"><LockIcon /></span>
                  <label className={newPassword ? 'float-up' : ''}>New password</label>
                  <button
                    type="button"
                    className="toggle-password-btn"
                    onClick={() => setShowNewPwd((s) => !s)}
                    aria-label={showNewPwd ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    {showNewPwd ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>

                <div className={`form-group ${actionFailed ? 'has-error' : ''}`}>
                  <input
                    type={showConfirmPwd ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                    onFocus={() => setIsPasswordFocused(true)}
                    onBlur={() => setIsPasswordFocused(false)}
                    placeholder=" "
                    required
                  />
                  <span className="input-icon"><LockIcon /></span>
                  <label className={confirmPassword ? 'float-up' : ''}>Confirm password</label>
                  <button
                    type="button"
                    className="toggle-password-btn"
                    onClick={() => setShowConfirmPwd((s) => !s)}
                    aria-label={showConfirmPwd ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    {showConfirmPwd ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>

                {error && <div className="fp-err">{error}</div>}

                <button type="submit" className="login-btn" disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner />
                      Resetting...
                    </>
                  ) : (
                    'Reset password'
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default ForgotPasswordPage;
